import { useEffect, useRef } from "react";
import { useAuthModule } from "@data-phone/react-resource-server-auth";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { setSessionData } from "../redux/slices/session.slice";
import { useAccounts } from "../services/accounts.service";
import { fetchDirectoryData, fetchIntegrationsData } from "../redux/thunks";
import { useGetIntegrationData } from "hooks/useGetIntegrationData";
import { fetchCallCenterAgents } from "../redux/thunks/CallCenterThunks/callCenterAgents";

export function useInitialLoader() {
  const { data: session, logout } = useAuthModule();
  const dispatch = useDispatch<AppDispatch>();
  const sessionData = useSelector((state: RootState) => state.session);
  const integrations = useSelector((state: RootState) => state.integrations.items);
  const account_id = sessionStorage.getItem("account_id");
  const { getIntegrationConfig } = useGetIntegrationData();

  const sessionProcessedRef = useRef(false);
  const lastDirectoryAccountId = useRef<string | null>(null);
  const lastIntegrationsAccountId = useRef<string | null>(null);
  const lastIntegrationConfigAccountId = useRef<string | null>(null);
  const lastCallCenterAgentsAccountId = useRef<string | null>(null);


  useEffect(() => {
  if (session?.sessionData) {
      dispatch(
        setSessionData({
          isAuthenticated: session.isAuthenticated,
          sessionData: {
            ...session.sessionData,
          },
        })
      );
  }
}, [session?.sessionData]);


  // ✅ 5. Accounts API
  const hasSubAccountPermission = sessionData?.sessionData?.permissions?.accounts?.includes("read:subAccount");
  const hasAdminPermission = sessionData?.sessionData?.permissions?.accounts?.includes("read:any");

  const accountsQuery = useAccounts(
    !hasAdminPermission ? session?.sessionData?.accountID : undefined,
    {
      enabled: !!sessionData?.sessionData && (hasAdminPermission || hasSubAccountPermission),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );


  // ✅ 2. Fetch directory data once per account_id
  useEffect(() => {
    if (!account_id || lastDirectoryAccountId.current === account_id) return;

    dispatch(fetchDirectoryData())
      .unwrap()
      .then(() => {
        lastDirectoryAccountId.current = account_id;
      })
      .catch((err) => console.error("❌ Directory fetch failed:", err));
  }, [dispatch, account_id]);

  // ✅ 3. Fetch integrations data once per account_id
  useEffect(() => {
    if (
      !account_id ||
      integrations.length > 0 ||
      lastIntegrationsAccountId.current === account_id
    ) {
      return;
    }

    dispatch(fetchIntegrationsData())
      .unwrap()
      .then(() => {
        lastIntegrationsAccountId.current = account_id;
      })
      .catch((err) => console.error("❌ Integrations fetch failed:", err));
  }, [dispatch, account_id, integrations.length]);

  // ✅ 4. Fetch integration config once per account_id when integrations exist
  useEffect(() => {
    if (
      integrations.length === 0 ||
      lastIntegrationConfigAccountId.current === account_id
    ) {
      return;
    }

    getIntegrationConfig();
    lastIntegrationConfigAccountId.current = account_id;
  }, [integrations, getIntegrationConfig, account_id]);

  


  const isReady = !!sessionData?.sessionData;

  return {
    isReady,
    session: sessionData,
    accounts: accountsQuery.data,
    accountsQuery,
    logout,
  };
}
